import React from "react";
import styled from "styled-components";

import { Shot, getShotPlaybackSrc, hasValidDeliveryDate } from "../../../../app/services/shots";
import { ShotStatus } from "../../../../types/home";
import TakeLabel from "../../../../components/label/TakeLabel";
// @ts-ignore -- JS モジュール（型定義なし）
import Counter from "../../../../components/counter/Counter";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../../components/date-display/DateDisplay";
import GenerativeAudioThumbnail from "../../../../components/media-preview/components/audio-thumbnail/GenerativeAudioThumbnail";
import { CoverVideo } from "../../../projects/components/ProjectPortraitCard";
import { WHEEL_SHOT_MEDIA_ATTR } from "../../../../utils/sharedMediaTransition";
import { getFileIconFromExtension } from "../../../../components/file-icon/fileIcon";
import {
  AUDIO_EXTENSIONS,
  IMAGE_EXTENSIONS,
  VIDEO_EXTENSIONS,
  extOfUrl,
} from "./mediaFileExt";

/**
 * HOME 進行中ホイールに並べる「やることショット」の縦型大判カード。
 *
 * ProjectPortraitCard と同じマガジン風レイヤー構成:
 *  - cover: shot.thumbnail を全面に敷く。無ければ media_file（プレビュー先頭の
 *          メディア）にフォールバック: 画像はそのまま cover、動画は先頭フレームの
 *          静止表示、オーディオは生成サムネイル（GenerativeAudioThumbnail。
 *          プレビューと同じ動的カラーで、選択中はメッシュがゆっくり動く）、
 *          それ以外はグレーのグラデーション + 種別アイコン
 *  - 動画: 選択中（ホイール中央）かつ media_file が動画のとき、Projects と同じ
 *          CoverVideo でミュートループ cover 再生する（ホバー再生は廃止）
 *  - 下部: スクリムの上にショット名と期限を重ねる
 *  - 右上: ステータスの垂れ帯（NEW = テイク1 / それ以外 = UPDATE）
 *
 * サイズは持たない（width/height: 100%）。実寸は RadioCardGroup の Slot が決める。
 */

const isVideoUrl = (url: string | null | undefined): url is string =>
  VIDEO_EXTENSIONS.includes(extOfUrl(url));

/** 生成サムネイルの静止絵用: 再生位置は常に 0 秒（安定参照）。 */
const zeroTime = () => 0;

const Frame = styled.div<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  outline: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  overflow: hidden;
  transition: border-color 0.2s, outline-color 0.2s;
`;

const CoverImage = styled.div<{ $url: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $url }) => $url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const CoverFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8e8e8 0%, #d4d4d4 100%);
`;

/** サムネイル未生成の動画 cover: 先頭フレームを静止表示する */
const CoverVideoStill = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** オーディオ cover: 生成サムネイル（絶対配置 fill）の載せ先。 */
const CoverAudio = styled.div`
  position: absolute;
  inset: 0;
`;

const BottomScrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 38.2%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
`;

const InfoLayer = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
  pointer-events: none;
`;

const ShotName = styled.span.attrs({
  className: "heading-spacing-18 u-line-height-150",
})`
  color: #fff;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
`;

const ShotDate = styled.span.attrs({ className: "bodytext-11" })`
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
`;

export interface ShotPortraitCardProps {
  shot: Shot;
  selected: boolean;
  /** やることリスト内のショット連番（TIPS カードを除いて数える。1 始まり）。 */
  number: number;
  /**
   * 選択中カードのみ true にしてメディアを動かす（動画はミュートループ再生、
   * オーディオは生成サムネイルのドリフト）。ProjectWheel と同じく
   * 「ホイール中央のカードだけ動く」の制御を親に委ねる。
   */
  playMedia?: boolean;
}

const ShotPortraitCard: React.FC<ShotPortraitCardProps> = ({
  shot,
  selected,
  number,
  playMedia = false,
}) => {
  const hasThumbnail = Boolean(shot.thumbnail);
  // 再生は変換済み（HLS）を優先する。原本 MP4 は数百 MB になりうるため
  const playbackSrc = getShotPlaybackSrc(shot);
  const videoSrc = playMedia && isVideoUrl(playbackSrc) ? playbackSrc : undefined;
  const showDeliveryDate = hasValidDeliveryDate(shot);

  // サムネイル未生成時のフォールバック（プレビュー先頭メディアの種別に合わせる）
  const mediaExt = extOfUrl(shot.media_file);
  const coverImageSrc =
    !hasThumbnail && IMAGE_EXTENSIONS.includes(mediaExt) ? shot.media_file : null;
  // オーディオは生成サムネイル。src は再生用 URL（プレビューと同じ優先順位）
  // なので、同じ音源なら他画面と同じ配色になる
  const coverAudioSrc =
    !hasThumbnail && AUDIO_EXTENSIONS.includes(mediaExt) ? playbackSrc : null;
  // HLS(m3u8) は静止フレーム表示に hls.js が必要なためアイコンにフォールバック
  const coverStillSrc =
    !hasThumbnail && VIDEO_EXTENSIONS.includes(mediaExt) && mediaExt !== "m3u8"
      ? shot.media_file
      : null;
  const fallbackIcon = getFileIconFromExtension(shot.media_file);

  return (
    <Frame $selected={selected} {...{ [WHEEL_SHOT_MEDIA_ATTR]: shot.id }}>
      {hasThumbnail ? (
        <CoverImage $url={shot.thumbnail as string} />
      ) : coverImageSrc ? (
        <CoverImage $url={coverImageSrc} />
      ) : coverAudioSrc ? (
        <CoverAudio>
          {/* 音は鳴らさないので isPlaying は常に false。選択中（ホイール中央）
              だけ isHovering で動きを許可し、動画の自動再生と揃える */}
          <GenerativeAudioThumbnail
            src={coverAudioSrc}
            isPlaying={false}
            isHovering={playMedia}
            glowTrigger="none"
            getCurrentTime={zeroTime}
          />
        </CoverAudio>
      ) : (
        <>
          <CoverFallback>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: "64px", color: "var(--soremo-light-gray)" }}
              aria-hidden="true"
            >
              {fallbackIcon}
            </span>
          </CoverFallback>
          {coverStillSrc && (
            <CoverVideoStill
              // #t=0.1: 先頭が黒フレームの動画対策（WaitingShotCloud と同じ）
              src={`${coverStillSrc}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
              tabIndex={-1}
            />
          )}
        </>
      )}

      {videoSrc && (
        <CoverVideo src={videoSrc} poster={shot.thumbnail ?? undefined} />
      )}

      {shot.status === ShotStatus.NEW ? (
        <TakeLabel take={1} />
      ) : (
        <TakeLabel label="UPDATE" />
      )}
      <Counter type="todo" number={number} />

      <BottomScrim />
      <InfoLayer>
        <ShotName translate="no">{shot.shot_name}</ShotName>
        <ShotDate>
          {showDeliveryDate ? (
            <DateDisplay
              date={shot.next_delivery_date!}
              type={DATE_DISPLAY_TYPE.DEADLINE_COUNTDOWN}
            />
          ) : (
            <DateDisplay
              date={shot.updated_at}
              type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
            />
          )}
        </ShotDate>
      </InfoLayer>
    </Frame>
  );
};

export default ShotPortraitCard;
